import React from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";

import CustomText from "../../../components/common/general/CustomText/CustomText";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { UserSessionAnalyticsData } from "../../../graphql/generated/graphql";

interface UserSessionAnalyticsTableProps {
  users: UserSessionAnalyticsData[];
  onUserSelect: (userId: string) => void;
}

const UserSessionAnalyticsTable: React.FC<UserSessionAnalyticsTableProps> = ({ users, onUserSelect }) => {
  const handleViewUser = (userId: string) => {
    onUserSelect(userId);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const getStudentInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  };

  return (
    <>
      <ScrollView style={styles.tableContainer} tabIndex={0}>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.studentNameCell]}>
              <CustomText text="Student Name" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.firstActiveDateCell]}>
              <CustomText text="First Active Date" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.lastActiveDateCell]}>
              <CustomText text="Last Active Date" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.totalSessionsCell]}>
              <CustomText text="Total Sessions" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.totalDurationCell]}>
              <CustomText text="Total Duration" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.averageDurationCell]}>
              <CustomText text="Avg Duration" size="s" weight="medium" style={styles.headerText} />
            </View>
          </View>

          {users.map((user) => (
            <View key={user.id} style={styles.tableRow}>
              <View style={[styles.cell, styles.studentNameCell]}>
                <View style={styles.studentNameContent}>
                  <View style={styles.studentAvatar}>
                    <CustomText
                      text={getStudentInitial(user.name)}
                      size="s"
                      weight="bold"
                      style={styles.avatarText}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.studentInfo}>
                    <TouchableOpacity style={styles.studentNameDetails} onPress={() => handleViewUser(user.id)}>
                      <CustomText
                        text={user.name}
                        weight="medium"
                        style={styles.studentName}
                        useTranslationText={false}
                      />
                      <ArrowSquareOutIcon size={16} color="#3E68FE" />
                    </TouchableOpacity>
                    <CustomText text={user.email} size="xs" style={styles.studentEmail} useTranslationText={false} />
                  </View>
                </View>
              </View>

              <View style={[styles.cell, styles.firstActiveDateCell]}>
                <CustomText
                  text={user.firstActiveDate ? formatDate(user.firstActiveDate) : "N/A"}
                  size="s"
                  style={styles.firstActiveDateText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.lastActiveDateCell]}>
                <CustomText
                  text={user.lastActiveDate ? formatDate(user.lastActiveDate) : "N/A"}
                  size="s"
                  style={styles.lastActiveDateText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.totalSessionsCell]}>
                <CustomText
                  text={user.totalSessions?.toString() || "0"}
                  size="s"
                  style={styles.numericText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.totalDurationCell]}>
                <CustomText
                  text={user.totalDuration ? formatDuration(user.totalDuration) : "0m"}
                  size="s"
                  style={styles.numericText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.averageDurationCell]}>
                <CustomText
                  text={user.averageDuration ? formatDuration(user.averageDuration) : "0m"}
                  size="s"
                  style={styles.numericText}
                  useTranslationText={false}
                />
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  studentNameCell: {
    flex: 3,
  },
  firstActiveDateCell: {
    flex: 1.5,
    alignItems: "flex-start",
  },
  lastActiveDateCell: {
    flex: 1.5,
    alignItems: "flex-start",
  },
  totalSessionsCell: {
    flex: 1,
    alignItems: "center",
  },
  totalDurationCell: {
    flex: 1.2,
    alignItems: "center",
  },
  averageDurationCell: {
    flex: 1.2,
    alignItems: "center",
  },
  headerText: {
    color: "#6B7280",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  studentNameContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  studentInfo: {
    flexDirection: "column",
    gap: 4,
  },
  studentNameDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  studentAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 14,
  },
  studentName: {
    color: "#3E68FE",
    fontSize: 15,
    cursor: "pointer",
  },
  studentEmail: {
    color: "#6B7280",
  },
  firstActiveDateText: {
    color: "#6B7280",
  },
  lastActiveDateText: {
    color: "#6B7280",
  },
  numericText: {
    color: "#374151",
    fontWeight: "500",
  },
  pillContainer: {
    alignSelf: "flex-start",
  },
  menu: {
    backgroundColor: "#fff",
    minWidth: 200,
  },
  menuItemText: {
    fontSize: 14,
    color: "#374151",
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 4,
  },
});

export default UserSessionAnalyticsTable;
